import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight, Delete, Upload } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { apiError, http } from "@/lib/api";
import { ClinicMark } from "@/components/Brand";
import { toast } from "sonner";

const STARTER = ["Consultation", "Scaling", "Filling (Composite)", "RCT", "Extraction",
  "Crown (PFM)", "Denture", "Braces Adjustment", "Implant", "Whitening", "Pulpectomy", "X-Ray (IOPA)"];
const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "⌫"];

export default function Setup() {
  const { saveClinic, clinic } = useApp();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    dentist_name: clinic?.dentist_name || "", name: clinic?.name || "", city: clinic?.city || "",
    logo_url: clinic?.logo_url || "", primary_color: clinic?.primary_color || "#2563EB",
    secondary_color: clinic?.secondary_color || "#0EA5A4",
    staff_code: clinic?.staff_code || "", quick_pin: clinic?.quick_pin || "",
    reminder_2h: clinic?.reminder_2h || false,
    treatments: clinic?.treatments?.length ? clinic.treatments : STARTER.slice(0, 8),
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const code = form.staff_code || "";
  const [uploading, setUploading] = useState(false);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Logo too large — use an image under 3MB");
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await http.post("/upload/logo", body);
      setForm((f) => ({ ...f, logo_url: data.logo_url }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(clinic ? apiError(err) : "Create the clinic first, then upload a logo from Settings");
    } finally {
      setUploading(false);
    }
  };

  const press = (k) => {
    if (k === "⌫") return setForm({ ...form, staff_code: code.slice(0, -1), quick_pin: code.slice(0, -1) });
    if (!k) return;
    const next = (code + k).slice(0, 4);
    setForm({ ...form, staff_code: next, quick_pin: next });
  };

  const toggleT = (t) =>
    setForm({ ...form, treatments: form.treatments.includes(t) ? form.treatments.filter((x) => x !== t) : [...form.treatments, t] });

  const finish = async () => {
    setBusy(true);
    try {
      const c = await saveClinic({ ...form, staff_code: code || undefined, quick_pin: code || "1234" });
      toast.success(`Clinic ready. Staff code ${c.staff_code}`);
      nav("/app");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen min-h-dvh">
      <div className="mx-auto max-w-2xl px-4 py-12 md:py-16">
        <div className="mb-8 flex items-center gap-3">
          <ClinicMark logo={form.logo_url} size={44} />
          <div>
            <p className="label-xs">Step {step} of 4</p>
            <h1 className="mt-1.5 dm-page-title">
              {step === 1 && <>Tell us about <span className="font-extrabold">your clinic</span></>}
              {step === 2 && <>Add your <span className="font-extrabold">logo</span></>}
              {step === 3 && <>Set a <span className="font-extrabold">staff code</span></>}
              {step === 4 && <>Starter <span className="font-extrabold">treatments</span></>}
            </h1>
          </div>
        </div>

        <div className="mb-7 h-1 overflow-hidden rounded-full bg-slate-100" data-testid="onb-progress">
          <div className="h-full rounded-full dm-accent-bg transition-all duration-500" style={{ width: `${(step / 4) * 100}%` }} />
        </div>

        <div className="dm-panel fade-up p-6 md:p-8" data-testid="setup-wizard">
          {step === 1 && (
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <p className="label-xs mb-1.5">Doctor name</p>
                <input data-testid="onb-doctor-name" className="dm-input" placeholder="Dr. Jane Doe" value={form.dentist_name} onChange={set("dentist_name")} />
              </div>
              <div>
                <p className="label-xs mb-1.5">Clinic name</p>
                <input data-testid="onb-clinic-name" className="dm-input" placeholder="Sunrise Dental" value={form.name} onChange={set("name")} />
              </div>
              <div>
                <p className="label-xs mb-1.5">City</p>
                <input data-testid="onb-city" className="dm-input" value={form.city} onChange={set("city")} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="label-xs mb-1.5">Primary color</p>
                  <input type="color" data-testid="onb-primary" className="dm-input h-11 p-1" value={form.primary_color} onChange={set("primary_color")} />
                </div>
                <div>
                  <p className="label-xs mb-1.5">Secondary color</p>
                  <input type="color" data-testid="onb-secondary" className="dm-input h-11 p-1" value={form.secondary_color} onChange={set("secondary_color")} />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">Step 2 of 4 — optional, you can change this later</p>
              <div className="flex flex-wrap items-center gap-4">
                <ClinicMark logo={form.logo_url} size={64} />
                <label className="dm-btn-ghost cursor-pointer">
                  <Upload size={15} /> {uploading ? "Uploading…" : "Upload logo"}
                  <input type="file" accept="image/*" data-testid="onb-logo-file" className="hidden" onChange={uploadLogo} disabled={uploading} />
                </label>
                {form.logo_url && (
                  <button type="button" data-testid="onb-logo-remove" onClick={() => setForm({ ...form, logo_url: "" })} className="dm-btn-quiet">Remove</button>
                )}
              </div>
              <div>
                <p className="label-xs mb-1.5">…or paste a logo URL</p>
                <input data-testid="onb-logo-url" className="dm-input" placeholder="https://…" value={form.logo_url || ""} onChange={set("logo_url")} />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5 text-center">
              <p className="text-sm text-slate-600">Step 3 of 4 — share this 4-digit code with your team so they can join</p>
              <div className="flex justify-center gap-3" data-testid="onb-pin-dots">
                {[0, 1, 2, 3].map((i) => (
                  <span key={i} className={`grid h-11 w-11 place-items-center rounded-xl border font-mono text-lg ${
                    code[i] ? "dm-badge-accent" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
                    {code[i] || "•"}
                  </span>
                ))}
              </div>
              <div className="mx-auto grid max-w-[260px] grid-cols-3 gap-2">
                {KEYS.map((k, i) => (
                  <button key={i} disabled={!k} data-testid={k ? `onb-key-${k === "⌫" ? "back" : k}` : undefined} onClick={() => press(k)}
                    className={`dm-btn-ghost !rounded-xl !py-3 font-mono text-lg ${!k ? "invisible" : ""}`}>
                    {k === "⌫" ? <Delete size={17} /> : k}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500">This code also unlocks Doctor Access on shared devices.</p>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">Pick what you actually do — you can edit this list later in Settings.</p>
              <div className="flex flex-wrap gap-2" data-testid="onb-treat-list">
                {STARTER.map((t) => {
                  const on = form.treatments.includes(t);
                  return (
                    <button key={t} data-testid={`onb-treat-${t.replace(/\W+/g, "-").toLowerCase()}`} onClick={() => toggleT(t)}
                      className={`dm-chip ${on ? "dm-chip-on" : ""}`}>
                      {on && <Check size={13} />}{t}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between gap-3 border-t border-slate-200 pt-6">
            <button data-testid="onb-back" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1} className="dm-btn-quiet disabled:opacity-30">Back</button>
            <div className="flex gap-2">
              {step === 2 && <button data-testid="onb-skip" onClick={() => setStep(3)} className="dm-btn-quiet">Skip for now</button>}
              {step < 4 ? (
                <button data-testid="onb-next" onClick={() => setStep(step + 1)}
                  disabled={(step === 1 && (!form.name || !form.dentist_name)) || (step === 3 && code.length !== 4)}
                  className="dm-btn-primary disabled:opacity-40">Continue <ArrowRight size={15} /></button>
              ) : (
                <button data-testid="onb-finish-btn" onClick={finish} disabled={busy} className="dm-btn-primary disabled:opacity-40">
                  {busy ? "Loading…" : "Create my clinic"} <Check size={15} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
